/**
 * Capa MapLibre para Vías Base de Referencia (OpenStreetMap)
 *
 * @module core/spatial/layers
 * @version 2.0
 * @created 2026-03-01
 * @updated 2026-03-21 - Migración de IGN a OSM
 *
 * @description
 * Sistema de capas para visualizar vías importadas de OpenStreetMap Argentina.
 * Las vías son de solo lectura y sirven como capa de referencia.
 *
 * Categorías OSM con estilos diferenciados:
 * - Motorway (autopistas): Gris azulado oscuro, 3px
 * - Trunk (troncales): Gris azulado medio, 2.5px
 * - Primary (primarias): Gris azulado, 2px
 * - Secondary (secundarias): Gris azulado claro, 1.5px
 * - Tertiary (terciarias): Gris muy claro, 1.2px
 * - Residential (residenciales): Gris casi imperceptible, 0.8px
 * - + 14 categorías más (service, footway, cycleway, etc.)
 */

import type { Map as MaplibreMap } from 'maplibre-gl';
import { baseNetworkApi } from '../services/base-network-api.service';
import { isMapReadyForSources } from '../../map-engine/MapEngineService';

/**
 * Estilos de línea por categoría de vía OSM
 *
 * @remarks
 * Paleta atenuada con tonos análogos gris/azul para capa de referencia.
 * Mantiene jerarquía visual (autopistas más oscuras → residenciales más claras)
 * pero con colores discretos que no compiten con capas principales.
 *
 * Categorías OSM:
 * - motorway: Autopistas (ej: Panamericana)
 * - trunk: Vías troncales (ej: Rutas Nacionales importantes)
 * - primary: Vías primarias (ej: Rutas Nacionales, avenidas principales)
 * - secondary: Vías secundarias (ej: Rutas Provinciales, avenidas)
 * - tertiary: Vías terciarias (ej: Calles colectoras)
 * - residential: Calles residenciales
 * - unclassified: Calles sin clasificar
 * - service: Calles de servicio
 */
const ROAD_STYLES = {
  // Vías principales (más visibles)
  motorway: {
    color: '#64748B', // slate-500 - Gris azulado medio
    width: 3,
    opacity: 0.7,
  },
  motorway_link: {
    color: '#64748B',
    width: 2,
    opacity: 0.65,
  },
  trunk: {
    color: '#78909C', // blue-grey-500
    width: 2.5,
    opacity: 0.65,
  },
  trunk_link: {
    color: '#78909C',
    width: 1.8,
    opacity: 0.6,
  },
  primary: {
    color: '#94A3B8', // slate-400
    width: 2,
    opacity: 0.6,
  },
  primary_link: {
    color: '#94A3B8',
    width: 1.5,
    opacity: 0.55,
  },
  secondary: {
    color: '#A8B4C8', // Gris azulado claro
    width: 1.5,
    opacity: 0.55,
  },
  secondary_link: {
    color: '#A8B4C8',
    width: 1.2,
    opacity: 0.5,
  },
  tertiary: {
    color: '#B8C5D8', // Gris azulado muy claro
    width: 1.2,
    opacity: 0.5,
  },
  // Vías locales (menos visibles)
  unclassified: {
    color: '#CBD5E1', // slate-300
    width: 1,
    opacity: 0.45,
  },
  residential: {
    color: '#D8E1EC', // Gris muy claro
    width: 0.8,
    opacity: 0.4,
  },
  living_street: {
    color: '#E2E8F0', // slate-200
    width: 0.7,
    opacity: 0.35,
  },
  service: {
    color: '#E8EEF5', // Casi imperceptible
    width: 0.5,
    opacity: 0.3,
  },
  // Caminos y senderos (muy sutiles)
  track: {
    color: '#F1F5F9', // slate-100
    width: 0.5,
    opacity: 0.25,
  },
  path: {
    color: '#F8FAFC', // slate-50
    width: 0.4,
    opacity: 0.2,
  },
  footway: {
    color: '#F8FAFC',
    width: 0.3,
    opacity: 0.15,
  },
  cycleway: {
    color: '#F0F9FF', // sky-50
    width: 0.4,
    opacity: 0.2,
  },
  steps: {
    color: '#F8FAFC',
    width: 0.3,
    opacity: 0.15,
  },
  // Especiales
  construction: {
    color: '#FEF3C7', // amber-100 - Amarillo muy claro
    width: 1,
    opacity: 0.3,
  },
  proposed: {
    color: '#E0E7FF', // indigo-100 - Azul muy claro
    width: 0.8,
    opacity: 0.25,
  },
};

/**
 * IDs de sources y layers
 */
export const BASE_NETWORK_SOURCE_ID = 'base-road-network-source';
export const BASE_NETWORK_LAYER_PREFIX = 'base-road-network-layer-';

/**
 * Encuentra el primer layer ID de labels/símbolos en el mapa
 * para insertar las capas de vías base debajo
 */
function findFirstSymbolLayer(map: MaplibreMap): string | undefined {
  const layers = map.getStyle()?.layers;
  if (!layers) return undefined;

  // Buscar el primer layer de tipo 'symbol' (labels, iconos)
  for (const layer of layers) {
    if (layer.type === 'symbol') {
      return layer.id;
    }
  }
  return undefined;
}

/**
 * Carga las vías base en el mapa
 *
 * @param map - Instancia del mapa MapLibre
 * @param projectId - ID del proyecto
 * @returns Promise que resuelve cuando las capas están cargadas
 */
export async function loadBaseNetworkLayers(
  map: MaplibreMap,
  projectId: string,
): Promise<void> {
  try {
    // 1. Obtener datos de vías base
    const geojson = await baseNetworkApi.getGeoJSON(projectId);

    // 2. Remover layers y source existentes si ya están cargados
    removeBaseNetworkLayers(map);

    // 3. Agregar source
    map.addSource(BASE_NETWORK_SOURCE_ID, {
      type: 'geojson',
      data: geojson as any,
    });

    // 4. Encontrar el primer layer de símbolos para insertar debajo
    const firstSymbolLayer = findFirstSymbolLayer(map);
    const allLayers = map.getStyle()?.layers || [];

    // Contar features por categoría para verificar que los filtros coincidirán
    const categoryDistribution: Record<string, number> = {};
    geojson.features?.forEach(f => {
      const cat = f.properties?.category || 'undefined';
      categoryDistribution[cat] = (categoryDistribution[cat] || 0) + 1;
    });

    console.log('[BaseNetworkLayer] Loading layers:', {
      beforeId: firstSymbolLayer || 'top',
      totalMapLayers: allLayers.length,
      featureCount: geojson.features?.length || 0,
      categoryDistribution,
      expectedCategories: Object.keys(ROAD_STYLES),
    });

    // 5. Crear una layer por cada categoría (con filtro)
    const categories = Object.keys(ROAD_STYLES) as Array<keyof typeof ROAD_STYLES>;

    categories.forEach(category => {
      const layerId = `${BASE_NETWORK_LAYER_PREFIX}${category}`;
      const style = ROAD_STYLES[category];

      map.addLayer(
        {
          id: layerId,
          type: 'line',
          source: BASE_NETWORK_SOURCE_ID,
          filter: ['==', ['get', 'category'], category],
          paint: {
            // Color base con soporte para estados hover y selected
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#2FA4B5', // WESTEER cyan para seleccionada
              ['boolean', ['feature-state', 'hovered'], false],
              '#60C5D6', // Cyan más claro para hover
              style.color, // Color base por categoría
            ],
            // Ancho con soporte para estados hover y selected
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              style.width + 2, // Más ancho si está seleccionada
              ['boolean', ['feature-state', 'hovered'], false],
              style.width + 1, // Ligeramente más ancho si está en hover
              style.width, // Ancho base
            ],
            // Opacidad con soporte para estados
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              1.0, // Completamente opaco si está seleccionada
              ['boolean', ['feature-state', 'hovered'], false],
              0.9, // Casi opaco si está en hover
              style.opacity, // Opacidad base
            ],
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        },
        firstSymbolLayer // Insertar debajo del primer layer de símbolos (labels)
      );
    });

    // Verificar que las capas se añadieron
    const layersAfter = map.getStyle()?.layers || [];
    const ourLayers = layersAfter.filter(l => l.id.startsWith(BASE_NETWORK_LAYER_PREFIX));
    console.log('[BaseNetworkLayer] ✅ Base network layers loaded:', {
      addedLayers: ourLayers.length,
      totalLayers: layersAfter.length,
      ourLayerIds: ourLayers.map(l => l.id),
    });
  } catch (error) {
    console.error('[BaseNetworkLayer] Error al cargar vías base:', error);
    throw error;
  }
}

/**
 * Remueve las capas de vías base del mapa
 *
 * @param map - Instancia del mapa MapLibre
 */
export function removeBaseNetworkLayers(map: MaplibreMap): void {
  const categories = Object.keys(ROAD_STYLES);

  // Remover layers
  categories.forEach(category => {
    const layerId = `${BASE_NETWORK_LAYER_PREFIX}${category}`;
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });

  // Remover source
  if (map.getSource(BASE_NETWORK_SOURCE_ID)) {
    map.removeSource(BASE_NETWORK_SOURCE_ID);
  }
}

/**
 * Muestra/oculta las capas de vías base
 *
 * @param map - Instancia del mapa MapLibre
 * @param visible - true para mostrar, false para ocultar
 */
export function setBaseNetworkVisibility(map: MaplibreMap, visible: boolean): void {
  const categories = Object.keys(ROAD_STYLES);
  const visibility = visible ? 'visible' : 'none';

  let found = 0;
  categories.forEach(category => {
    const layerId = `${BASE_NETWORK_LAYER_PREFIX}${category}`;
    const layer = map.getLayer(layerId);
    if (layer) {
      map.setLayoutProperty(layerId, 'visibility', visibility);
      found++;
    }
  });

  console.log(`[BaseNetworkLayer] 👁️ Visibility set to "${visibility}" for ${found}/${categories.length} layers`);
}

/**
 * Verifica si las capas de vías base están cargadas en el mapa
 *
 * @param map - Instancia del mapa MapLibre
 * @returns true si las capas están cargadas
 */
export function areBaseNetworkLayersLoaded(map: MaplibreMap): boolean {
  if (!isMapReadyForSources(map)) return false;
  try {
    return map.getSource(BASE_NETWORK_SOURCE_ID) !== undefined;
  } catch {
    return false;
  }
}
